import React, { useEffect, useRef } from 'react';
import { TouchableOpacity, Text, StyleSheet, ActivityIndicator, ViewStyle, TextStyle, Platform, View } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { LinearGradient } from 'expo-linear-gradient';
import { colors, typographyTokens } from '../../styles';
import { useTheme } from '../../context/ThemeContext';
import { ctaShadow, ctaSurface } from './CtaGradientFill';

import { ON_ACCENT_TEXT } from '../../styles/accentText';
interface ButtonProps {
    children: React.ReactNode;
    onPress?: () => void;
    variant?: 'primary' | 'secondary' | 'ghost' | 'outline' | 'solar' | 'convex-fire' | 'convex-light';
    iconColor?: string;
    size?: 'sm' | 'md' | 'lg';
    isLoading?: boolean;
    disabled?: boolean;
    showArrow?: boolean;
    style?: ViewStyle | any;
    textStyle?: TextStyle | any;
    leftIcon?: keyof typeof Ionicons.glyphMap;
    rightIcon?: keyof typeof Ionicons.glyphMap;
    icon?: React.ReactNode;
    testID?: string;
    /**
     * CORE-964 — без этого react-native-web рисует кнопку обычным `div`: без
     * роли, без tabindex, не в фокусируемом наборе. Такая кнопка не нажимается
     * ни с клавиатуры, ни экранной читалкой — именно так «Start inviting» в
     * приветственном окне Фонда оказался тупиком для QA. С ролью RNW отдаёт
     * настоящий `<button>`, и Enter/Space начинают работать сами.
     */
    accessibilityRole?: 'button' | 'link';
    accessibilityLabel?: string;
}

export const Button: React.FC<ButtonProps> = ({
    children,
    onPress,
    variant = 'primary',
    iconColor,
    size = 'md',
    isLoading = false,
    disabled = false,
    showArrow = false,
    style,
    textStyle,
    leftIcon,
    rightIcon,
    icon,
    testID,
    accessibilityRole,
    accessibilityLabel,
}) => {
    const { colors: themeColors, isDark } = useTheme();

    const getButtonStyle = () => {
        const baseStyle: any[] = [styles.button, styles[size]];

        // primary = high-contrast inverted fill. themeColors.charcoal is
        // #181411 in light (identical to before) but the *inverted* near-black
        // #F5F0EB in dark — without this the button is invisible on the dark
        // canvas (#17181C). Light mode is unchanged pixel-for-pixel.
        if (variant === 'primary') baseStyle.push(styles.primary, { backgroundColor: themeColors.charcoal, borderColor: themeColors.charcoal });
        if (variant === 'secondary') baseStyle.push(styles.secondary, isDark && { borderColor: 'rgba(255,255,255,0.15)' });
        if (variant === 'outline') baseStyle.push(styles.outline, isDark && { borderColor: themeColors.textPrimary });
        if (variant === 'ghost') baseStyle.push(styles.ghost);
        if (variant === 'solar') baseStyle.push(styles.solar);

        // Elite Variants
        if (variant === 'convex-fire') baseStyle.push(styles.convexFire);
        if (variant === 'convex-light') baseStyle.push(styles.convexLight);

        if (disabled || isLoading) baseStyle.push(styles.disabled);

        return [baseStyle, style];
    };

    const getIconColor = () => {
        if (iconColor) return iconColor;
        // CORE-1733 (возврат QA) — на оранжевой заливке (solar, convex-fire, CTA-градиент)
        // текст и глиф — токен ON_ACCENT_TEXT: белый там 2.7–2.8:1.
        if (variant === 'convex-fire') return ON_ACCENT_TEXT;
        if (variant === 'solar') return ON_ACCENT_TEXT;
        // primary text/icon contrasts the inverted charcoal fill: white in
        // light (#FFFFFF), dark in dark (#17181C).
        if (variant === 'primary') return themeColors.canvas;
        return themeColors.textPrimary;
    };

    const getTextStyle = () => {
        const baseStyle: any[] = [styles.text, styles[`text_${size}` as keyof typeof styles]];

        if (variant === 'solar') baseStyle.push(styles.textPrimary);
        if (variant === 'primary') baseStyle.push({ color: themeColors.canvas });
        if (variant === 'convex-fire') baseStyle.push(styles.textConvexFire);
        if (variant === 'secondary' || variant === 'convex-light' || variant === 'outline' || variant === 'ghost') baseStyle.push({ color: themeColors.textPrimary });
        if (variant === 'primary' || variant === 'solar' || variant === 'convex-fire') {
            baseStyle.push(styles.textPrimaryWeight);
        }

        if (Platform.OS !== 'web') return [baseStyle, textStyle];
        const flattened = StyleSheet.flatten(textStyle) as TextStyle | undefined;
        const {
            fontFamily: _fontFamily,
            fontSize: _fontSize,
            fontStyle: _fontStyle,
            fontWeight: _fontWeight,
            letterSpacing: _letterSpacing,
            lineHeight: _lineHeight,
            textTransform: _textTransform,
            ...safeTextStyle
        } = flattened ?? {};
        return [baseStyle, safeTextStyle];
    };

    const iconSize = size === 'sm' ? 14 : size === 'lg' ? 22 : 18;

    // CORE-964 — клавиатура. Роли оказалось мало: с ней react-native-web отдаёт
    // настоящий `<button>`, кнопка получает фокус и рамку, но нажатие Enter не
    // доходит до `onPress` (проверено на превью: фокус есть, окно открыто).
    // Пропой это тоже не чинится — `TouchableOpacity` подставляет свои
    // обработчики ПОСЛЕ чужих пропсов (`_extends({}, rest, pressEventHandlers)`),
    // так что переданный `onKeyDown` затирается.
    //
    // Поэтому слушатель вешается прямо на узел: его перекрыть нечем. Событие
    // гасится, чтобы до делегированного обработчика React оно не дошло и
    // нажатие не сработало дважды.
    //
    // Только там, где вызывающий объявил роль: без роли узел не фокусируется,
    // слушателю всё равно нечего слушать, а трогать все кнопки приложения ради
    // этого незачем.
    const hostRef = useRef<any>(null);
    useEffect(() => {
        if (Platform.OS !== 'web' || !accessibilityRole) return;
        const node = hostRef.current as unknown as HTMLElement | null;
        if (!node || typeof node.addEventListener !== 'function') return;

        const onKeyDown = (event: any) => {
            if (event?.key !== 'Enter' && event?.key !== ' ' && event?.key !== 'Spacebar') return;
            if (disabled || isLoading || !onPress) return;
            event.preventDefault?.();
            event.stopPropagation?.();
            onPress();
        };

        node.addEventListener('keydown', onKeyDown);
        return () => node.removeEventListener('keydown', onKeyDown);
    }, [accessibilityRole, disabled, isLoading, onPress]);

    return (
        <TouchableOpacity
            onPress={onPress}
            disabled={disabled || isLoading}
            activeOpacity={0.8}
            style={getButtonStyle() as any}
            className={Platform.OS === 'web' ? (variant.startsWith('convex') ? variant : '') : ''}
            testID={testID}
            accessibilityRole={accessibilityRole}
            accessibilityLabel={accessibilityLabel}
            ref={hostRef}
        >
            {(variant === 'solar' || variant === 'convex-fire') && (
                <LinearGradient
                    colors={[themeColors.ctaStart, themeColors.ctaEnd]}
                    start={{ x: 0, y: 0.5 }}
                    end={{ x: 1, y: 0.5 }}
                    style={StyleSheet.absoluteFill}
                    pointerEvents="none"
                />
            )}
            <View style={styles.buttonContent}>
                {isLoading && <ActivityIndicator color={getIconColor()} />}
                {leftIcon && <Ionicons name={leftIcon as any} size={iconSize} color={getIconColor()} />}
                {icon}
                <Text style={getTextStyle() as any}>{children}</Text>
                {showArrow && <Ionicons name="arrow-forward" size={iconSize} color={getIconColor()} />}
                {rightIcon && !showArrow && <Ionicons name={rightIcon as any} size={iconSize} color={getIconColor()} />}
            </View>
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'transparent',
        ...Platform.select({
            web: {
                transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                cursor: 'pointer',
            },
        }),
    },
    buttonContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
    },
    primary: {
        backgroundColor: colors.charcoal,
        borderColor: colors.charcoal,
    },
    secondary: {
        backgroundColor: 'transparent',
        borderColor: 'rgba(24, 20, 17, 0.15)',
    },
    solar: {
        borderColor: 'transparent',
        borderRadius: 999,
        overflow: 'hidden',
        ...(Platform.OS === 'web' ? {
            backgroundColor: 'transparent',
        } : {
            // The gradient is the fill; the flat colour under it keeps Android's
            // elevation shadow alive (a transparent background would kill it).
            ...ctaSurface,
            ...ctaShadow,
        }),
    },
    convexFire: {
        borderRadius: 999,
        overflow: 'hidden',
        ...(Platform.OS === 'web' ? {
            backgroundColor: 'transparent',
            boxShadow: '0 14px 34px rgba(184, 83, 42, 0.22)',
        } : {
            // CORE-974 parity: web gets the glow from `boxShadow` above.
            ...ctaSurface,
            ...ctaShadow,
        }),
    },
    convexLight: {
        ...(Platform.OS !== 'web' && {
            backgroundColor: '#FFFFFF',
            borderColor: 'rgba(0,0,0,0.1)',
        }),
    },
    outline: {
        backgroundColor: 'transparent',
        borderWidth: 1.5,
        borderColor: colors.charcoal,
    },
    ghost: {
        backgroundColor: 'transparent',
    },
    disabled: {
        opacity: 0.3,
    },
    sm: {
        paddingHorizontal: 20,
        paddingVertical: 10,
    },
    md: {
        paddingHorizontal: 32,
        paddingVertical: 16,
    },
    lg: {
        paddingHorizontal: 48,
        paddingVertical: 22,
    },
    text: {
        ...typographyTokens.buttonSecondary,
    },
    text_sm: {},
    text_md: {},
    text_lg: {},

    textPrimary: {
        color: ON_ACCENT_TEXT,
    },
    textPrimaryWeight: {
        ...typographyTokens.buttonPrimary,
    },
    textConvexFire: {
        color: ON_ACCENT_TEXT,
    },
    textSecondary: {
        color: colors.charcoal,
    },
});
